import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../api";

// Platform admin: switch practice features on or off for one org
// (backend core/features.py). Each change saves immediately.
const CHANNEL_LABEL = { sms: "SMS", whatsapp: "WhatsApp", email: "Email" };

// Which patient-alert channels this org may use (backend notifications/patient_notify.py).
// A channel switched off here is hidden from the clinic and never sends.
function AlertChannels({ orgId }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  useEffect(() => { api.getOrgAlertChannels(orgId).then((r) => setData(r.data)).catch(() => setData(null)); }, [orgId]);
  if (!data) return null;
  const toggle = async (c) => {
    setSaving(c);
    try { setData((await api.updateOrgAlertChannels(orgId, { [c]: !data.enabled.includes(c) })).data); }
    catch (e) { toast.error(e?.response?.data?.error || "Couldn't save."); }
    finally { setSaving(null); }
  };
  return (
    <div className="border-t border-gray-100 pt-4">
      <p className="text-sm font-semibold text-gray-800">Alert channels</p>
      <p className="text-xs text-gray-500 mb-2">In-app alerts are always on. Switch a channel on only once it's set up for this clinic. It's then offered in Settings → Patient alerts and on staff crisis alerts.</p>
      <div className="flex flex-wrap gap-3">
        {data.available.map((c) => (
          <label key={c} className="normal-case font-sans tracking-normal inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm">
            <input type="checkbox" checked={data.enabled.includes(c)} disabled={saving === c} onChange={() => toggle(c)} />
            {CHANNEL_LABEL[c] || c}
            {!data.configured[c] && <span className="text-[11px] text-amber-600">(no sender configured on the server)</span>}
          </label>
        ))}
      </div>
    </div>
  );
}

// How long a doctor may run over an online session (backend
// scheduling/session_limits.py): length of one extension and how many they get.
function SessionExtensions({ orgId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.getOrgSessionLimits(orgId).then((r) => { setData(r.data); setForm(r.data.limits); }).catch(() => setData(null));
  }, [orgId]);
  if (!data || !form) return null;
  const range = (k) => data.ranges?.[k] || [0, 30];
  const changed = form.extension_minutes !== data.limits.extension_minutes || form.max_extensions !== data.limits.max_extensions;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.setOrgSessionLimits(orgId, {
        extension_minutes: Number(form.extension_minutes), max_extensions: Number(form.max_extensions),
      });
      setData(r.data); setForm(r.data.limits);
      toast.success("Session extensions saved");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };
  const num = (k) => (
    <input type="number" min={range(k)[0]} max={range(k)[1]} value={form[k]}
      onChange={(e) => setForm({ ...form, [k]: e.target.value === "" ? "" : Number(e.target.value) })}
      className="w-20 rounded-lg border border-gray-200 px-2 py-1.5 text-sm" />
  );
  return (
    <div className="border-t border-gray-100 pt-4">
      <p className="text-sm font-semibold text-gray-800">Session extensions</p>
      <p className="text-xs text-gray-500 mb-2">
        From 5 minutes before an online session ends, the doctor sees a countdown and can extend it. When no extensions are left, the call ends for everyone at the end time.
      </p>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-700">
        <span className="inline-flex items-center gap-2">Each extension {num("extension_minutes")} minutes</span>
        <span className="inline-flex items-center gap-2">At most {num("max_extensions")} times per session</span>
        <button type="button" disabled={!changed || saving} onClick={save}
          className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-700 disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <p className="mt-1 text-[11px] text-gray-400">
        Allowed: {range("extension_minutes")[0]}-{range("extension_minutes")[1]} minutes, {range("max_extensions")[0]}-{range("max_extensions")[1]} times (0 = no extensions).
      </p>
    </div>
  );
}

// The clinic's page in the patient app, /c/<name> (backend
// organization/patient_portal.py). Private also leaves its doctors out of
// MindConnect's public search and keeps its patients on its own clinic.
function ClinicPortal({ orgId }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.getOrgPortal(orgId).then((r) => { setData(r.data); setForm({ slug: r.data.slug, private: r.data.private }); })
      .catch(() => setData(null));
  }, [orgId]);
  if (!data || !form) return null;
  const changed = form.slug.trim().toLowerCase() !== data.slug || form.private !== data.private;
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.setOrgPortal(orgId, { slug: form.slug, private: form.private });
      setData(r.data); setForm({ slug: r.data.slug || "", private: r.data.private });
      toast.success("Clinic page saved");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="border-t border-gray-100 pt-4">
      <p className="text-sm font-semibold text-gray-800">Clinic page</p>
      <p className="text-xs text-gray-500 mb-2">
        Every clinic has a page listing only its doctors, with its own name and logo; each doctor's booking link shows the clinic first too.
        Optionally give it a readable name; without one it uses a short id.
      </p>
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span className="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden">
          <span className="px-2 py-1.5 bg-gray-50 text-gray-500 border-r border-gray-200">…/c/</span>
          <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="sunrise-clinic (optional)"
            className="w-48 px-2 py-1.5 text-sm outline-none" />
        </span>
        <button type="button" disabled={!changed || saving} onClick={save}
          className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-primary-700 disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <label className="normal-case font-sans tracking-normal mt-2 flex items-start gap-2 text-sm text-gray-700">
        <input type="checkbox" className="mt-1" checked={form.private} onChange={(e) => setForm({ ...form, private: e.target.checked })} />
        <span>
          Private clinic: leave its doctors out of MindConnect's public search, don't offer "explore other doctors" on its pages, and show its patients only this clinic after they sign in.
          <span className="block text-xs text-gray-500">A patient who is also with a listed clinic still sees the public search.</span>
        </span>
      </label>
      {data.url && (
        <p className="mt-1 text-xs text-gray-500">Clinic page: <a href={data.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">{data.url}</a></p>
      )}
    </div>
  );
}

export default function OrgFeaturesPanel({ orgId }) {
  const [features, setFeatures] = useState(null);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    api.fetchOrgFeatures(orgId).then((r) => setFeatures(r.data)).catch(() => setFeatures([]));
  }, [orgId]);

  const toggle = async (f) => {
    setSaving(f.key);
    try {
      const res = await api.updateOrgFeatures(orgId, { [f.key]: !f.enabled });
      setFeatures(res.data);
      toast.success(`${f.label} switched ${f.enabled ? "off" : "on"}`);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't change this feature.");
    } finally {
      setSaving(null);
    }
  };

  if (!features) return <p className="text-sm text-gray-500">Loading features…</p>;
  const available = features.filter((f) => f.available);
  const coming = features.filter((f) => !f.available);

  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 space-y-5">
      <div>
        <h3 className="text-xl font-semibold text-gray-800">3. Features</h3>
        <p className="text-sm text-gray-500 mt-1">New practice features ship switched off. Turn them on per organisation once you're ready. Changes save immediately.</p>
      </div>

      {available.length === 0 ? (
        <p className="text-sm text-gray-500 rounded-lg border border-dashed border-gray-300 p-4">No features are ready to switch on yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {available.map((f) => (
            <li key={f.key} className="flex items-start justify-between gap-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-800">{f.label}</p>
                <p className="text-xs text-gray-500">{f.description}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={f.enabled}
                aria-label={`${f.label}: ${f.enabled ? "on" : "off"}`}
                onClick={() => toggle(f)}
                disabled={saving === f.key}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors disabled:opacity-60 ${f.enabled ? "bg-blue-600" : "bg-gray-300"}`}
              >
                <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transition-transform ${f.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <AlertChannels orgId={orgId} />
      <SessionExtensions orgId={orgId} />
      <ClinicPortal orgId={orgId} />

      {coming.length > 0 && (
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Coming soon</p>
          <div className="flex flex-wrap gap-2">
            {coming.map((f) => (
              <span key={f.key} title={f.description} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">{f.label}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
